import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';

/**
 * Fetch the organization's report with optional filtering
 * @param {Object} organization - Organization to get report for
 * @param {Object} options - Optional filtering options
 * @param {string} options.rangeType - Range type (thisweek, lastweek, etc.)
 * @param {string} options.startDate - Start date for filtering
 * @param {string} options.endDate - End date for filtering
 * @param {string} options.reportType - Specific report type (engagement, sentiment, tone, clarity)
 * @param {Array} options.channels - Array of channel IDs to filter by
 */
export const fetchReport = async (organization, options = {}) => {
  if (!organization) {
    console.log('Missing required organization parameter');
    return null;
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const params = {};
    if (options.rangeType) params.rangeType = options.rangeType;
    // Only add date filters if explicitly requested in options
    if (options.startDate && options.includeFiltering) params.startDate = options.startDate;
    if (options.endDate && options.includeFiltering) params.endDate = options.endDate;
    if (options.reportType) params.reportType = options.reportType;
    // Add channel filtering
    if (options.channels && options.channels.length > 0) {
      params.channels = options.channels.join(',');
    }
    
    console.log('Fetching report with params:', params);
    
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    if (response.data.success) {
      console.log('Successfully fetched report data');
      console.log('Report data keys:', Object.keys(response.data.data.reportData || {}));
      if (response.data.data.reportData?.engagement) {
        console.log('Engagement data keys:', Object.keys(response.data.data.reportData.engagement));
      }
      return {
        success: true,
        data: response.data.data,
        lastSynced: response.data.lastSynced ? new Date(response.data.lastSynced) : null
      };
    }
    
    return {
      success: false,
      message: 'Failed to fetch report data'
    };
  } catch (error) {
    // Handle 404 (no report found) without logging an error
    if (error.response && error.response.status === 404) {
      console.log('No report found');
      return {
        success: false,
        message: 'No report found'
      };
    }
    
    console.error('Error fetching report:', error);
    return {
      success: false,
      message: 'Error fetching report data'
    };
  }
};

/**
 * Fetch report data filtered by specific date range
 * @param {Object} organization - Organization to get report for
 * @param {string} startDate - Start date (YYYY-MM-DD format)
 * @param {string} endDate - End date (YYYY-MM-DD format)
 * @param {string} reportType - Optional specific report type
 */
export const fetchReportByDateRange = async (organization, startDate, endDate, reportType = null) => {
  if (!organization || !startDate || !endDate) {
    console.log('Missing required parameters for date range fetch');
    return null;
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const params = { startDate, endDate };
    if (reportType) params.reportType = reportType;
    
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}/date-range`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    if (response.data.success) {
      console.log('Successfully fetched filtered report data');
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    }
    
    return {
      success: false,
      message: 'Failed to fetch filtered report data'
    };
  } catch (error) {
    console.error('Error fetching filtered report:', error);
    return {
      success: false,
      message: 'Error fetching filtered report data'
    };
  }
};

/**
 * Generate a new report by triggering the backend sync process
 */
export const generateReport = async (organization, dateRange = {}) => {
  if (!organization) {
    console.log('Missing required organization parameter');
    return null;
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    // Default to last week if no date range provided
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    const requestBody = {
      startDate: dateRange.startDate || startDate.toISOString(),
      endDate: dateRange.endDate || endDate.toISOString(),
      rangeType: dateRange.rangeType || 'lastweek'
    };
    
    const response = await axios.post(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      requestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    );
    
    if (response.data.success) {
      console.log('Successfully generated new report');
      return {
        success: true,
        data: response.data.data,
        lastSynced: new Date(response.data.data.updatedAt)
      };
    }
    
    return {
      success: false,
      message: 'Failed to generate report'
    };
  } catch (error) {
    console.error('Error generating report:', error);
    throw error;
  }
};

/**
 * Create or update a report with new data
 */
export const createOrUpdateReport = async (organization, reportData, dateRange) => {
  if (!organization || !reportData || !dateRange) {
    console.log('Missing required parameters for report creation/update');
    return null;
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const response = await axios.put(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        dateRange,
        reportData
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    );
    
    if (response.data.success) {
      console.log('Successfully created/updated report');
      return {
        success: true,
        data: response.data.data
      };
    }
    
    return {
      success: false,
      message: 'Failed to create/update report'
    };
  } catch (error) {
    console.error('Error creating/updating report:', error);
    return {
      success: false,
      message: 'Error creating/updating report'
    };
  }
};

/**
 * Check the sync status for an organization
 */
export const getSyncStatus = async (organization) => {
  if (!organization) {
    return {
      lastSynced: null,
      hasReports: false
    };
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}/sync-status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    );
    
    if (response.data.success) {
      return {
        lastSynced: response.data.data.lastSynced ? new Date(response.data.data.lastSynced) : null,
        hasReports: response.data.data.hasReports
      };
    }
    
    return {
      lastSynced: null,
      hasReports: false
    };
  } catch (error) {
    console.error('Error fetching sync status:', error);
    return {
      lastSynced: null,
      hasReports: false
    };
  }
};

/**
 * Calculate total engagement score from daily scores (frontend calculation)
 * @param {Object} engagementScores - Daily engagement scores object
 * @returns {number} Average engagement score
 */
export const calculateTotalEngagementScore = (engagementScores) => {
  if (!engagementScores || Object.keys(engagementScores).length === 0) {
    return 0;
  }
  
  const scores = Object.values(engagementScores);
  const total = scores.reduce((sum, score) => sum + score, 0);
  return total / scores.length;
};

/**
 * Calculate peak activity from hourly activity data (frontend calculation)
 * @param {Object} activityData - Daily activity data with hourly breakdown
 * @returns {Object} Peak activity information
 */
export const calculatePeakActivity = (activityData) => {
  if (!activityData || Object.keys(activityData).length === 0) {
    return {
      hour: null,
      day: null,
      level: 'very low'
    };
  }
  
  const activityLevels = {
    'very low': 1,
    'low': 2,
    'medium': 3,
    'high': 4,
    'very high': 5
  };
  
  let maxLevel = 0;
  let peakHour = null;
  let peakDay = null;
  
  Object.entries(activityData).forEach(([dateStr, hourlyData]) => {
    Object.entries(hourlyData).forEach(([hour, level]) => {
      const levelValue = activityLevels[level] || 1;
      if (levelValue > maxLevel) {
        maxLevel = levelValue;
        peakHour = hour;
        peakDay = dateStr;
      }
    });
  });
  
  return {
    hour: peakHour,
    day: peakDay,
    level: Object.keys(activityLevels).find(key => activityLevels[key] === maxLevel) || 'very low'
  };
};

/**
 * Calculate peak activity for each channel (frontend calculation)
 * @param {Object} channelEngagementData - Channel engagement data
 * @returns {Object} Peak activity for each channel
 */
export const calculateChannelPeakActivity = (channelEngagementData) => {
  if (!channelEngagementData) {
    return {};
  }
  
  const channelPeaks = {};
  
  Object.entries(channelEngagementData).forEach(([channelName, channelData]) => {
    let maxEngagement = 0;
    let peakDate = null;
    let peakActivity = 'very low';
    
    Object.entries(channelData).forEach(([dateStr, metrics]) => {
      if (metrics.engagementScore > maxEngagement) {
        maxEngagement = metrics.engagementScore;
        peakDate = dateStr;
        peakActivity = metrics.activity;
      }
    });
    
    channelPeaks[channelName] = {
      date: peakDate,
      engagementScore: maxEngagement,
      activity: peakActivity
    };
  });
  
  return channelPeaks;
};

/**
 * Filter report data by time period (frontend filtering)
 * @param {Object} reportData - Full report data
 * @param {string} timePeriod - Time period filter ('lastweek', 'lastmonth', etc.)
 * @returns {Object} Filtered report data
 */
export const filterReportByTimePeriod = (reportData, timePeriod) => {
  if (!reportData || !timePeriod) {
    return reportData;
  }
  
  const now = new Date();
  let startDate;
  
  switch (timePeriod) {
    case 'lastweek':
      startDate = new Date();
      startDate.setDate(now.getDate() - 7);
      break;
    case 'lastmonth':
      startDate = new Date();
      startDate.setMonth(now.getMonth() - 1);
      break;
    case 'last30days':
      startDate = new Date();
      startDate.setDate(now.getDate() - 30);
      break;
    case 'last3months':
      startDate = new Date();
      startDate.setMonth(now.getMonth() - 3);
      break;
    case 'last6months':
      startDate = new Date();
      startDate.setMonth(now.getMonth() - 6);
      break;
    default:
      return reportData; // No filtering
  }
  
  const filteredData = JSON.parse(JSON.stringify(reportData));
  
  // Filter engagement data
  if (filteredData.engagement) {
    if (filteredData.engagement.engagementScore) {
      const filteredScores = {};
      Object.entries(filteredData.engagement.engagementScore).forEach(([dateStr, score]) => {
        const date = parseDateString(dateStr);
        if (date >= startDate) {
          filteredScores[dateStr] = score;
        }
      });
      filteredData.engagement.engagementScore = filteredScores;
    }
    
    // Similar filtering for activity and channelEngagement...
  }
  
  return filteredData;
};

/**
 * Helper function to parse date strings in the format "Jun 1 2025, Monday"
 */
export const parseDateString = (dateStr) => {
  try {
    const datePart = dateStr.split(',')[0].trim();
    return new Date(datePart);
  } catch (error) {
    console.error('Error parsing date string:', dateStr, error);
    return new Date();
  }
};

/**
 * Get detailed report data for display (legacy compatibility)
 */
export const getDetailedReportData = async (organization) => {
  console.log('Using legacy getDetailedReportData - consider using fetchReport instead');
  return await fetchReport(organization);
};

/**
 * Calculate top frequent words across all channels for a given time period
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @param {number} limit - Maximum number of words to return (default: 10)
 * @returns {Array} Array of {text, count, sentiment} objects sorted by frequency
 */
export const calculateTopFrequentWords = (reportData, startDate, endDate, limit = 10) => {
  if (!reportData?.sentiment?.frequentWords) {
    console.log('No frequent words data available in report');
    return [];
  }

  // Parse start and end dates for filtering
  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  // Aggregate word frequencies across all dates in the range
  const wordFrequency = new Map();
  const wordSentiment = new Map();
  
  // Process frequent words from daily data
  Object.entries(reportData.sentiment.frequentWords).forEach(([dateStr, wordsArray]) => {
    // Parse the date string format "Jun 23 2025, Monday"
    const datePart = dateStr.split(',')[0].trim();
    const messageDate = new Date(datePart);
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    // Aggregate words from this day
    if (Array.isArray(wordsArray)) {
      wordsArray.forEach(wordData => {
        const word = wordData.text;
        const count = wordData.count || 1;
        const sentiment = wordData.sentiment || 'neutral';
        
        // Update word frequency
        wordFrequency.set(word, (wordFrequency.get(word) || 0) + count);
        
        // Track sentiment for this word
        if (!wordSentiment.has(word)) {
          wordSentiment.set(word, { positive: 0, negative: 0, neutral: 0, total: 0 });
        }
        const sentimentData = wordSentiment.get(word);
        sentimentData[sentiment] = (sentimentData[sentiment] || 0) + count;
        sentimentData.total += count;
      });
    }
  });
  
  // Convert to array and get top words by frequency
  const wordsData = Array.from(wordFrequency.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, parseInt(limit))
    .map(([word, count]) => {
      const sentimentData = wordSentiment.get(word) || { positive: 0, negative: 0, neutral: 0, total: 0 };
      
      // Determine dominant sentiment for this word
      let dominantSentiment = 'neutral';
      if (sentimentData.positive > sentimentData.negative && sentimentData.positive > sentimentData.neutral) {
        dominantSentiment = 'positive';
      } else if (sentimentData.negative > sentimentData.positive && sentimentData.negative > sentimentData.neutral) {
        dominantSentiment = 'negative';
      }
      
      return {
        text: word,
        count,
        sentiment: dominantSentiment
      };
    });
  
  console.log(`Calculated ${wordsData.length} top frequent words for date range ${startDate} - ${endDate}`);
  return wordsData;
};

/**
 * Calculate tone time series data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of daily tone data for charts
 */
export const calculateToneTimeData = (reportData, startDate, endDate) => {
  if (!reportData?.tone?.toneScore) {
    console.log('No tone data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  const timeData = [];
  
  Object.entries(reportData.tone.toneScore).forEach(([dateStr, toneScores]) => {
    const datePart = dateStr.split(',')[0].trim();
    // Parse date parts manually to avoid timezone-driven date shifts from toISOString()
    const [mName, mDay, mYear] = datePart.split(' ');
    const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    const monthIdx = MONTH_NUMS[mName];
    const dayNum = parseInt(mDay);
    const yearNum = parseInt(mYear);
    const messageDate = new Date(yearNum, monthIdx, dayNum); // local midnight, safe for comparison
    const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    const scores = toneScores || {};
    const raw = {
      collaborative: (scores.collaborative || 0) * 100,
      creative: (scores.creative || 0) * 100,
      formal: (scores.formal || 0) * 100,
      assertive: (scores.assertive || 0) * 100,
      urgent: (scores.urgent || 0) * 100,
      informal: (scores.informal || 0) * 100,
      casual: (scores.casual || 0) * 100,
      technical: (scores.technical || 0) * 100,
    };

    // Normalize so all 8 tones sum to 100% for stacked bar display
    const rawTotal = Object.values(raw).reduce((s, v) => s + v, 0);
    const normalized = {};
    for (const key of Object.keys(raw)) {
      normalized[key] = rawTotal > 0
        ? parseFloat(((raw[key] / rawTotal) * 100).toFixed(1))
        : 0;
    }

    // Keep weighted score for backward compatibility (Dashboard)
    const overallScore = (
      (scores.collaborative || 0) * 0.2 +
      (scores.creative || 0) * 0.15 +
      (scores.formal || 0) * 0.15 +
      (scores.informal || 0) * 0.1 +
      (scores.casual || 0) * 0.1 +
      (scores.technical || 0) * 0.1 +
      (1 - (scores.urgent || 0)) * 0.1 +
      (1 - (scores.assertive || 0)) * 0.1
    );

    timeData.push({
      date: isoDate,
      score: parseFloat((overallScore * 100).toFixed(1)),
      ...normalized,
    });
  });
  
  // Sort by date
  timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log(`Calculated ${timeData.length} tone time data points for date range ${startDate} - ${endDate}`);
  return timeData;
};

/**
 * Calculate tone distribution data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of tone distribution data for radar chart
 */
export const calculateToneDistributionData = (reportData, startDate, endDate) => {
  if (!reportData?.tone?.toneScore) {
    console.log('No tone data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  let totalCollaborative = 0;
  let totalCreative = 0;
  let totalFormal = 0;
  let totalAssertive = 0;
  let totalUrgent = 0;
  let totalInformal = 0;
  let totalCasual = 0;
  let totalTechnical = 0;
  let totalDays = 0;
  
  Object.entries(reportData.tone.toneScore).forEach(([dateStr, toneScores]) => {
    const datePart = dateStr.split(',')[0].trim();
    const messageDate = new Date(datePart);
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    const scores = toneScores || {};
    totalCollaborative += scores.collaborative || 0;
    totalCreative += scores.creative || 0;
    totalFormal += scores.formal || 0;
    totalAssertive += scores.assertive || 0;
    totalUrgent += scores.urgent || 0;
    totalInformal += scores.informal || 0;
    totalCasual += scores.casual || 0;
    totalTechnical += scores.technical || 0;
    totalDays++;
  });
  
  if (totalDays === 0) return [];
  
  const distributionData = [
    { name: "Urgent",        value: Math.round((totalUrgent / totalDays) * 100),        color: "#FF5252" },
    { name: "Assertive",     value: Math.round((totalAssertive / totalDays) * 100),     color: "#FF9100" },
    { name: "Formal",        value: Math.round((totalFormal / totalDays) * 100),        color: "#C6FF00" },
    { name: "Informal",      value: Math.round((totalInformal / totalDays) * 100),      color: "#00E676" },
    { name: "Technical",     value: Math.round((totalTechnical / totalDays) * 100),     color: "#00E5FF" },
    { name: "Collaborative", value: Math.round((totalCollaborative / totalDays) * 100), color: "#448AFF" },
    { name: "Creative",      value: Math.round((totalCreative / totalDays) * 100),      color: "#7C4DFF" },
    { name: "Casual",        value: Math.round((totalCasual / totalDays) * 100),        color: "#FF4081" },
  ];
  
  console.log(`Calculated tone distribution for ${totalDays} days`);
  return distributionData;
};

/**
 * Calculate tone distribution data with actual message counts from raw data
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of tone distribution data with actual message counts
 */
export const calculateToneDistributionDataWithCounts = (reportData, startDate, endDate) => {
  // First try to calculate from processed tone data if available
  if (reportData?.tone?.toneScore) {
    console.log('Using processed tone data for distribution calculation');
    
    const filterStartDate = startDate ? new Date(startDate) : null;
    const filterEndDate = endDate ? new Date(endDate) : null;
    
    let totalCollaborative = 0;
    let totalCreative = 0;
    let totalFormal = 0;
    let totalAssertive = 0;
    let totalUrgent = 0;
    let totalInformal = 0;
    let totalCasual = 0;
    let totalTechnical = 0;
    let totalDays = 0;
    
    Object.entries(reportData.tone.toneScore).forEach(([dateStr, toneScores]) => {
      const datePart = dateStr.split(',')[0].trim();
      const messageDate = new Date(datePart);
      
      // Check if this date is in the filter range
      if (filterStartDate && messageDate < filterStartDate) return;
      if (filterEndDate && messageDate > filterEndDate) return;
      
      const scores = toneScores || {};
      // Scores are in 0-1 range, convert to percentages for display
      totalCollaborative += (scores.collaborative || 0) * 100;
      totalCreative += (scores.creative || 0) * 100;
      totalFormal += (scores.formal || 0) * 100;
      totalAssertive += (scores.assertive || 0) * 100;
      totalUrgent += (scores.urgent || 0) * 100;
      totalInformal += (scores.informal || 0) * 100;
      totalCasual += (scores.casual || 0) * 100;
      totalTechnical += (scores.technical || 0) * 100;
      totalDays++;
    });
    
    if (totalDays === 0) {
      return [
        { name: "Urgent",        value: 0, messageCount: 0, color: "#FF5252" },
        { name: "Assertive",     value: 0, messageCount: 0, color: "#FF9100" },
        { name: "Formal",        value: 0, messageCount: 0, color: "#C6FF00" },
        { name: "Informal",      value: 0, messageCount: 0, color: "#00E676" },
        { name: "Technical",     value: 0, messageCount: 0, color: "#00E5FF" },
        { name: "Collaborative", value: 0, messageCount: 0, color: "#448AFF" },
        { name: "Creative",      value: 0, messageCount: 0, color: "#7C4DFF" },
        { name: "Casual",        value: 0, messageCount: 0, color: "#FF4081" },
      ];
    }
    
    // Calculate average scores and convert to distribution percentages
    const avgScores = {
      collaborative: totalCollaborative / totalDays,
      creative: totalCreative / totalDays,
      formal: totalFormal / totalDays,
      assertive: totalAssertive / totalDays,
      urgent: totalUrgent / totalDays,
      informal: totalInformal / totalDays,
      casual: totalCasual / totalDays,
      technical: totalTechnical / totalDays
    };
    
    const totalScore = Object.values(avgScores).reduce((sum, score) => sum + score, 0);
    
    const distributionData = [
      { name: "Urgent",        value: totalScore > 0 ? Math.round((avgScores.urgent        / totalScore) * 100) : 0, messageCount: Math.round(avgScores.urgent),        color: "#FF5252" },
      { name: "Assertive",     value: totalScore > 0 ? Math.round((avgScores.assertive     / totalScore) * 100) : 0, messageCount: Math.round(avgScores.assertive),     color: "#FF9100" },
      { name: "Formal",        value: totalScore > 0 ? Math.round((avgScores.formal        / totalScore) * 100) : 0, messageCount: Math.round(avgScores.formal),        color: "#C6FF00" },
      { name: "Informal",      value: totalScore > 0 ? Math.round((avgScores.informal      / totalScore) * 100) : 0, messageCount: Math.round(avgScores.informal),      color: "#00E676" },
      { name: "Technical",     value: totalScore > 0 ? Math.round((avgScores.technical     / totalScore) * 100) : 0, messageCount: Math.round(avgScores.technical),     color: "#00E5FF" },
      { name: "Collaborative", value: totalScore > 0 ? Math.round((avgScores.collaborative / totalScore) * 100) : 0, messageCount: Math.round(avgScores.collaborative), color: "#448AFF" },
      { name: "Creative",      value: totalScore > 0 ? Math.round((avgScores.creative      / totalScore) * 100) : 0, messageCount: Math.round(avgScores.creative),      color: "#7C4DFF" },
      { name: "Casual",        value: totalScore > 0 ? Math.round((avgScores.casual        / totalScore) * 100) : 0, messageCount: Math.round(avgScores.casual),        color: "#FF4081" },
    ];
    
    console.log('Calculated tone distribution from processed data:', distributionData);
    return distributionData;
  }
}

// Modified function to calculate tone distribution without rawData
export const calculateToneDistributionFromMessages = (reportData, startDate, endDate) => {
  // Use processed report data for tone distribution calculation
  console.log('Using processed report data for tone distribution calculation (no rawData)');
  return calculateToneDistributionData(reportData, startDate, endDate).map(item => ({
    ...item,
    messageCount: Math.round(item.value / 100 * 50) // Estimated from percentage
  }));
};

/**
 * Extract topic cloud data from report (date-based structure)
 * @param {Object} reportData - Report data from the database
 * @param {string} startDate - Start date for filtering
 * @param {string} endDate - End date for filtering
 */
export const extractTopicCloudData = (reportData, startDate, endDate) => {
  if (!reportData?.topics?.channelTopics) {
    console.log('No channel topics data found in report');
    return [];
  }

  const channelTopics = reportData.topics.channelTopics;
  console.log('Raw channel topics data for topic cloud:', channelTopics);
  console.log('Filtering by date range:', startDate, 'to', endDate);
  
  // Aggregate topics across all channels and the selected date range
  const topicAggregation = {};
  
  // Iterate through all channels
  Object.keys(channelTopics).forEach(channelName => {
    const channelDates = channelTopics[channelName];
    
    // Get all dates for this channel, filter by date range
    const allDates = Object.keys(channelDates);
    const filteredDates = allDates.filter(date => isDateInReportRange(date, startDate, endDate));
    
    console.log(`Filtered dates for channel ${channelName}:`, filteredDates);
    
    // Aggregate topics from all filtered dates for this channel
    filteredDates.forEach(date => {
      const dayTopics = channelDates[date];
      
      // Handle array of topics
      if (Array.isArray(dayTopics)) {
        dayTopics.forEach(topic => {
          const topicName = topic.name;
          const cluster = topic.cluster;
          
          if (!topicAggregation[topicName]) {
            topicAggregation[topicName] = {
              frequency: 0,
              cluster: cluster,
              internalExternalSum: 0,
              internalExternalWeight: 0,
              operationalStrategicSum: 0,
              operationalStrategicWeight: 0
            };
          }
          
          const weight = typeof topic.value === 'number' ? topic.value : 1;
          topicAggregation[topicName].frequency += weight;

          if (typeof topic.internalExternal === 'number') {
            topicAggregation[topicName].internalExternalSum += topic.internalExternal * weight;
            topicAggregation[topicName].internalExternalWeight += weight;
          }
          if (typeof topic.operationalStrategic === 'number') {
            topicAggregation[topicName].operationalStrategicSum += topic.operationalStrategic * weight;
            topicAggregation[topicName].operationalStrategicWeight += weight;
          }
        });
      }
    });
  });
  
  // Convert to the format expected by TopicCloudChart
  const cloudData = Object.keys(topicAggregation).map(topicName => ({
    topic: topicName,
    frequency: topicAggregation[topicName].frequency,
    category: topicAggregation[topicName].cluster,
    internalExternal:
      topicAggregation[topicName].internalExternalWeight > 0
        ? Math.round(
            topicAggregation[topicName].internalExternalSum /
              topicAggregation[topicName].internalExternalWeight
          )
        : undefined,
    operationalStrategic:
      topicAggregation[topicName].operationalStrategicWeight > 0
        ? Math.round(
            topicAggregation[topicName].operationalStrategicSum /
              topicAggregation[topicName].operationalStrategicWeight
          )
        : undefined
  }));
  
  console.log('Processed topic cloud data from channel topics:', cloudData);
  return cloudData;
};

/**
 * Parse date string from database format to ISO date
 * @param {string} dateStr - Date string like "May 16 2025, Friday"
 */
const parseReportDate = (dateStr) => {
  try {
    // Remove the day name and parse the date
    const cleanDateStr = dateStr.replace(/,\s*\w+$/, ''); // Remove ", Friday" part
    const date = new Date(cleanDateStr);
    
    if (isNaN(date.getTime())) {
      console.warn('Could not parse date:', dateStr);
      return null;
    }
    
    // Return in YYYY-MM-DD format for consistency
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Error parsing date:', dateStr, error);
    return null;
  }
};

/**
 * Check if a date is within the specified range
 * @param {string} dateStr - Date to check
 * @param {string} startDate - Start date (ISO string)
 * @param {string} endDate - End date (ISO string)
 */
const isDateInReportRange = (dateStr, startDate, endDate) => {
  const parsedDate = parseReportDate(dateStr);
  if (!parsedDate) return false;
  
  const start = new Date(startDate).toISOString().split('T')[0];
  const end = new Date(endDate).toISOString().split('T')[0];
  
  return parsedDate >= start && parsedDate <= end;
};

/**
 * Extract topic trends data from topic cloud data
 * @param {Object} reportData - Report data from the database
 * @param {string} startDate - Start date for filtering
 * @param {string} endDate - End date for filtering
 */
export const extractTopicTrendsData = (reportData, startDate, endDate) => {
  if (!reportData?.topics?.channelTopics) {
    console.log('No channel topics data found in report');
    return [];
  }

  const channelTopics = reportData.topics.channelTopics;
  console.log('Raw channel topics data for trends:', channelTopics);
  console.log('Filtering by date range:', startDate, 'to', endDate);
  
  // Convert to the format expected by TopicTrendsChart
  const trendsData = [];
  
  // Collect all unique dates across all channels
  const allDates = new Set();
  Object.keys(channelTopics).forEach(channelName => {
    const channelDates = channelTopics[channelName];
    Object.keys(channelDates).forEach(date => {
      allDates.add(date);
    });
  });
  
  // Get all dates that are in the filter range
  const filteredDates = Array.from(allDates).filter(date => 
    isDateInReportRange(date, startDate, endDate)
  );
  
  // Sort dates
  const sortedDates = filteredDates.sort((a, b) => {
    const dateA = parseReportDate(a);
    const dateB = parseReportDate(b);
    return dateA && dateB ? dateA.localeCompare(dateB) : 0;
  });
  
  console.log('Filtered dates:', sortedDates);
  
  sortedDates.forEach(date => {
    const parsedDate = parseReportDate(date);
    
    if (!parsedDate) {
      console.warn('Skipping invalid date:', date);
      return;
    }
    
    // Initialize cluster counts
    const clusterCounts = {
      productDevelopment: 0,
      customerSupport: 0,
      operationsProcess: 0,
      businessStrategy: 0,
      communicationCulture: 0
    };
    
    // Count topics by cluster for this date across all channels
    Object.keys(channelTopics).forEach(channelName => {
      const channelDates = channelTopics[channelName];
      const dayTopics = channelDates[date];
      
      if (dayTopics && Array.isArray(dayTopics)) {
        dayTopics.forEach(topic => {
          const cluster = topic.cluster;
          if (clusterCounts.hasOwnProperty(cluster)) {
            clusterCounts[cluster] += 1;
          }
        });
      }
    });
    
    const dataPoint = {
      date: parsedDate, // Use parsed date in YYYY-MM-DD format
      ...clusterCounts
    };
    
    trendsData.push(dataPoint);
  });
  
  console.log('Processed topic trends data from channel topics:', trendsData);
  return trendsData;
};

/**
 * Extract topic distribution data from report (channel-based structure)
 * @param {Object} reportData - Report data from the database
 * @param {string} startDate - Start date for filtering
 * @param {string} endDate - End date for filtering
 * @returns {Array} Array of cluster distribution data
 */
export const extractTopicDistributionData = (reportData, startDate, endDate) => {
  if (!reportData?.topics?.channelTopics) {
    console.log('No channel topics data found in report');
    return [];
  }

  const channelTopics = reportData.topics.channelTopics;
  console.log('Raw channel topics data for topic distribution:', channelTopics);
  console.log('Filtering by date range:', startDate, 'to', endDate);
  
  // Aggregate clusters across all channels and the selected date range
  const clusterAggregation = {};
  
  // Iterate through all channels
  Object.keys(channelTopics).forEach(channelName => {
    const channelDates = channelTopics[channelName];
    
    // Get all dates for this channel, filter by date range
    const allDates = Object.keys(channelDates);
    const filteredDates = allDates.filter(date => isDateInReportRange(date, startDate, endDate));
    
    // Aggregate clusters from all filtered dates for this channel
    filteredDates.forEach(date => {
      const dayTopics = channelDates[date];
      
      // Handle array of topics
      if (dayTopics && Array.isArray(dayTopics)) {
        dayTopics.forEach(topic => {
          const cluster = topic.cluster;
          
          if (!clusterAggregation[cluster]) {
            clusterAggregation[cluster] = 0;
          }
          
          clusterAggregation[cluster] += 1;
        });
      }
    });
  });
  
  // Convert to the format expected by TopicDistributionChart
  const distributionData = Object.keys(clusterAggregation).map(cluster => ({
    cluster: cluster,
    count: clusterAggregation[cluster]
  }));
  
  console.log('Processed topic distribution data from channel topics:', distributionData);
  return distributionData;
};

/**
 * Extract channel topics data from report (date-based structure)
 * @param {Object} reportData - Report data from the database
 * @param {string} startDate - Start date for filtering
 * @param {string} endDate - End date for filtering
 */
export const extractChannelTopicsData = (reportData, startDate, endDate) => {
  if (!reportData?.topics?.channelTopics) {
    console.log('No channel topics data found in report');
    return [];
  }

  const channelTopics = reportData.topics.channelTopics;
  console.log('Raw channel topics data:', channelTopics);
  console.log('Filtering by date range:', startDate, 'to', endDate);

  // Define cluster display names
  const clusterDisplayNames = {
    productDevelopment: 'Product & Development',
    customerSupport: 'Customer & Support',
    operationsProcess: 'Operations & Process',
    businessStrategy: 'Business & Strategy',
    communicationCulture: 'Communication & Culture'
  };

  // Prepare result array
  const channelData = [];

  Object.keys(channelTopics).forEach(channelName => {
    const channelDates = channelTopics[channelName];
    const topicAggregation = {};
    const clusterCounts = {};

    // Get all dates for this channel, filter by date range
    const allDates = Object.keys(channelDates);
    const filteredDates = allDates.filter(date => isDateInReportRange(date, startDate, endDate));

    // Aggregate topics from all filtered dates for this channel
    filteredDates.forEach(date => {
      const dayTopics = channelDates[date];
      if (dayTopics && Array.isArray(dayTopics)) {
        dayTopics.forEach(topic => {
          const topicName = topic.name;
          const cluster = topic.cluster;

          // Aggregate topics
          if (!topicAggregation[topicName]) {
            topicAggregation[topicName] = {
              value: 0,
              cluster: cluster,
              internalExternalSum: 0,
              internalExternalWeight: 0,
              operationalStrategicSum: 0,
              operationalStrategicWeight: 0
            };
          }
          const weight = typeof topic.value === 'number' ? topic.value : 1;
          topicAggregation[topicName].value += weight;

          if (typeof topic.internalExternal === 'number') {
            topicAggregation[topicName].internalExternalSum +=
              topic.internalExternal * weight;
            topicAggregation[topicName].internalExternalWeight += weight;
          }
          if (typeof topic.operationalStrategic === 'number') {
            topicAggregation[topicName].operationalStrategicSum +=
              topic.operationalStrategic * weight;
            topicAggregation[topicName].operationalStrategicWeight += weight;
          }

          // Count cluster frequencies
          clusterCounts[cluster] = (clusterCounts[cluster] || 0) + weight;
        });
      }
    });

    // Skip channels with no topics
    if (Object.keys(topicAggregation).length === 0) {
      return;
    }

    // Find dominant cluster
    let topCluster = '';
    let maxClusterValue = 0;
    Object.keys(clusterCounts).forEach(cluster => {
      if (clusterCounts[cluster] > maxClusterValue) {
        maxClusterValue = clusterCounts[cluster];
        topCluster = cluster;
      }
    });

    // Calculate total value for percentage calculation
    const totalValue = Object.values(topicAggregation).reduce((sum, topic) => sum + topic.value, 0);

    // All topics with share of channel mentions + cluster (for table chips and drawer)
    const allTopicsSorted = Object.keys(topicAggregation)
      .map((topicName) => {
        const clusterKey = topicAggregation[topicName].cluster;
        return {
          name: topicName,
          value: topicAggregation[topicName].value,
          percentage:
            totalValue > 0
              ? Math.round((topicAggregation[topicName].value / totalValue) * 100)
              : 0,
          cluster: clusterKey,
          clusterDisplayName: clusterDisplayNames[clusterKey] || clusterKey,
          internalExternal:
            topicAggregation[topicName].internalExternalWeight > 0
              ? Math.round(
                  topicAggregation[topicName].internalExternalSum /
                    topicAggregation[topicName].internalExternalWeight
                )
              : undefined,
          operationalStrategic:
            topicAggregation[topicName].operationalStrategicWeight > 0
              ? Math.round(
                  topicAggregation[topicName].operationalStrategicSum /
                    topicAggregation[topicName].operationalStrategicWeight
                )
              : undefined
        };
      })
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));

    // Calculate dominant cluster percentage
    const clusterPercentage = totalValue > 0 ? Math.round((maxClusterValue / totalValue) * 100) : 0;

    // Display name for cluster
    const topClusterDisplay = clusterDisplayNames[topCluster] || topCluster;

    // Sum of messages actually stored/processed per day (from engagement), same dates as topics.
    // Lets users compare Slack totals to per-topic AI `value` fields, which are model estimates.
    let messagesInPeriod = 0;
    const channelEngagementByDay =
      reportData?.engagement?.channelEngagement?.[channelName];
    if (channelEngagementByDay && typeof channelEngagementByDay === 'object') {
      filteredDates.forEach((dateKey) => {
        const day = channelEngagementByDay[dateKey];
        if (day && typeof day.numberOfMessages === 'number') {
          messagesInPeriod += day.numberOfMessages;
        }
      });
    }

    channelData.push({
      channel: channelName,
      topTopic: topClusterDisplay,
      percentage: clusterPercentage,
      topics: allTopicsSorted,
      messagesInPeriod,
      is_private: false // If you have privacy info, set it here
    });
  });

  console.log('Processed topics data from channel topics:', channelData);
  return channelData;
};

/**
 * Calculate channel tone data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of channel tone data for table
 */
export const calculateChannelToneData = (reportData, startDate, endDate) => {
  if (!reportData?.tone?.channelTone) {
    console.log('No channel tone data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  const channelData = [];
  
  Object.entries(reportData.tone.channelTone).forEach(([channelName, channelDates]) => {
    let totalCollaborative = 0;
    let totalCreative = 0;
    let totalFormal = 0;
    let totalAssertive = 0;
    let totalUrgent = 0;
    let totalInformal = 0;
    let totalCasual = 0;
    let totalTechnical = 0;
    let totalDays = 0;
    
    Object.entries(channelDates).forEach(([dateStr, toneScores]) => {
      const datePart = dateStr.split(',')[0].trim();
      const messageDate = new Date(datePart);
      
      // Check if this date is in the filter range
      if (filterStartDate && messageDate < filterStartDate) return;
      if (filterEndDate && messageDate > filterEndDate) return;
      
      const scores = toneScores || {};
      totalCollaborative += scores.collaborative || 0;
      totalCreative += scores.creative || 0;
      totalFormal += scores.formal || 0;
      totalAssertive += scores.assertive || 0;
      totalUrgent += scores.urgent || 0;
      totalInformal += scores.informal || 0;
      totalCasual += scores.casual || 0;
      totalTechnical += scores.technical || 0;
      totalDays++;
    });
    
    if (totalDays > 0) {
      const avgCollaborative = totalCollaborative / totalDays;
      const avgCreative = totalCreative / totalDays;
      const avgFormal = totalFormal / totalDays;
      const avgAssertive = totalAssertive / totalDays;
      const avgUrgent = totalUrgent / totalDays;
      const avgInformal = totalInformal / totalDays;
      const avgCasual = totalCasual / totalDays;
      const avgTechnical = totalTechnical / totalDays;
      
      // Calculate overall tone score (weighted average matching backend logic)
      const overallScore = (
        (avgCollaborative * 0.2) +
        (avgCreative * 0.15) +
        (avgFormal * 0.15) +
        (avgInformal * 0.1) +
        (avgCasual * 0.1) +
        (avgTechnical * 0.1) +
        ((1 - avgUrgent) * 0.1) + // Less urgent = better
        ((1 - avgAssertive) * 0.1) // Less assertive = better tone
      ) * 100;
      
      // Determine dominant tone from all 8 tones
      const tones = {
        collaborative: avgCollaborative,
        creative: avgCreative,
        formal: avgFormal,
        assertive: avgAssertive,
        urgent: avgUrgent,
        informal: avgInformal,
        casual: avgCasual,
        technical: avgTechnical
      };
      const dominantTone = Object.keys(tones).reduce((a, b) => tones[a] > tones[b] ? a : b);
      
      // Generate frequent tones array for display (all 8, sorted by score)
      const frequentTones = Object.entries(tones)
        .sort(([,a], [,b]) => b - a)
        .map(([tone, score]) => ({
          tone: tone,
          text: tone.charAt(0).toUpperCase() + tone.slice(1),
          value: Math.round(score * 100)
        }));
      
      channelData.push({
        id: channelName,
        channel: channelName,
        score: Math.round(overallScore), // Show score as percentage (e.g., 58%)
        dominantTone: dominantTone,
        frequentTones: frequentTones,
        collaborative: Math.round(avgCollaborative * 100),
        creative: Math.round(avgCreative * 100),
        formal: Math.round(avgFormal * 100),
        assertive: Math.round(avgAssertive * 100),
        urgent: Math.round(avgUrgent * 100),
        informal: Math.round(avgInformal * 100),
        casual: Math.round(avgCasual * 100),
        technical: Math.round(avgTechnical * 100),
        is_private: false // Will be enriched with actual value later
      });
    }
  });
  
  console.log(`Calculated ${channelData.length} channel tone data points`);
  return channelData;
};

/**
 * Calculate clarity time series data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of daily clarity data for charts
 */
export const calculateClarityTimeData = (reportData, startDate, endDate) => {
  if (!reportData?.clarity?.clarityScore) {
    console.log('No clarity data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  const timeData = [];
  
  Object.entries(reportData.clarity.clarityScore).forEach(([dateStr, clarityScore]) => {
    const datePart = dateStr.split(',')[0].trim();
    // Parse date parts manually to avoid timezone-driven date shifts from toISOString()
    const [mName, mDay, mYear] = datePart.split(' ');
    const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    const monthIdx = MONTH_NUMS[mName];
    const dayNum = parseInt(mDay);
    const yearNum = parseInt(mYear);
    const messageDate = new Date(yearNum, monthIdx, dayNum); // local midnight, safe for comparison
    const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    // Get clarity metrics for this date
    const metrics = reportData.clarity.clarityMetrics?.[dateStr] || {};
    
    timeData.push({
      date: isoDate,
      score: parseFloat((clarityScore * 100).toFixed(1)), // Convert to percentage (98.5 instead of 0.985)
      complexity: Math.round((metrics.complexity || 0) * 100),
      questionFrequency: Math.round((metrics.questionFrequency || 0) * 100),
      actionItemFrequency: Math.round((metrics.actionItemFrequency || 0) * 100)
    });
  });
  
  // Sort by date
  timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log(`Calculated ${timeData.length} clarity time data points for date range ${startDate} - ${endDate}`);
  return timeData;
};

/**
 * Calculate clarity distribution data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of clarity distribution data for charts
 */
export const calculateClarityDistributionData = (reportData, startDate, endDate) => {
  if (!reportData?.clarity?.clarityScore) {
    console.log('No clarity data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  let veryHigh = 0;
  let high = 0;
  let medium = 0;
  let low = 0;
  let veryLow = 0;
  let totalDays = 0;
  
  Object.entries(reportData.clarity.clarityScore).forEach(([dateStr, clarityScore]) => {
    const datePart = dateStr.split(',')[0].trim();
    const messageDate = new Date(datePart);
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    const scorePercent = clarityScore * 100;
    
    if (scorePercent >= 80) veryHigh++;
    else if (scorePercent >= 60) high++;
    else if (scorePercent >= 40) medium++;
    else if (scorePercent >= 20) low++;
    else veryLow++;
    
    totalDays++;
  });
  
  if (totalDays === 0) return [];
  
  const distributionData = [
    { name: "Very Clear", value: Math.round((veryHigh / totalDays) * 100), color: "#14B882" },
    { name: "Clear", value: Math.round((high / totalDays) * 100), color: "#22C55E" },
    { name: "Moderate", value: Math.round((medium / totalDays) * 100), color: "#F59E0B" },
    { name: "Unclear", value: Math.round((low / totalDays) * 100), color: "#EF4444" },
    { name: "Very Unclear", value: Math.round((veryLow / totalDays) * 100), color: "#E50026" }
  ];
  
  console.log(`Calculated clarity distribution for ${totalDays} days`);
  return distributionData;
};

/**
 * Calculate clarity distribution data in format expected by ClarityDistributionChart component
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of clarity distribution data for bar chart
 */
export const calculateClarityDistributionDataForChart = (reportData, startDate, endDate) => {
  if (!reportData?.clarity?.clarityScore) {
    console.log('No clarity data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  let veryHigh = 0;
  let high = 0;
  let medium = 0;
  let low = 0;
  let veryLow = 0;
  let totalDays = 0;
  
  Object.entries(reportData.clarity.clarityScore).forEach(([dateStr, clarityScore]) => {
    const datePart = dateStr.split(',')[0].trim();
    const messageDate = new Date(datePart);
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    const scorePercent = clarityScore * 100;
    
    if (scorePercent >= 80) veryHigh++;
    else if (scorePercent >= 60) high++;
    else if (scorePercent >= 40) medium++;
    else if (scorePercent >= 20) low++;
    else veryLow++;
    
    totalDays++;
  });
  
  if (totalDays === 0) return [];
  
  // Format data for the ClarityDistributionChart component
  const distributionData = [
    { 
      range: "80-100%", 
      displayRange: "Very clear",
      count: veryHigh, 
      percentage: totalDays > 0 ? Math.round((veryHigh / totalDays) * 100) : 0 
    },
    { 
      range: "60-79%", 
      displayRange: "Clear",
      count: high, 
      percentage: totalDays > 0 ? Math.round((high / totalDays) * 100) : 0 
    },
    { 
      range: "40-59%", 
      displayRange: "Moderate",
      count: medium, 
      percentage: totalDays > 0 ? Math.round((medium / totalDays) * 100) : 0 
    },
    { 
      range: "20-39%", 
      displayRange: "Unclear",
      count: low, 
      percentage: totalDays > 0 ? Math.round((low / totalDays) * 100) : 0 
    },
    { 
      range: "0-19%", 
      displayRange: "Very unclear",
      count: veryLow, 
      percentage: totalDays > 0 ? Math.round((veryLow / totalDays) * 100) : 0 
    }
  ];
  
  console.log(`Calculated clarity distribution for chart: ${totalDays} days`, distributionData);
  return distributionData;
};

/**
 * Calculate channel clarity data from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of channel clarity data for table
 */
export const calculateChannelClarityData = (reportData, startDate, endDate) => {
  if (!reportData?.clarity?.channelClarity) {
    console.log('No channel clarity data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  const channelData = [];
  
  Object.entries(reportData.clarity.channelClarity).forEach(([channelName, channelDates]) => {
    let totalClarityScore = 0;
    let totalComplexity = 0;
    let totalQuestionFreq = 0;
    let totalActionItemFreq = 0;
    let totalDays = 0;
    
    Object.entries(channelDates).forEach(([dateStr, clarityMetrics]) => {
      const datePart = dateStr.split(',')[0].trim();
      const messageDate = new Date(datePart);
      
      // Check if this date is in the filter range
      if (filterStartDate && messageDate < filterStartDate) return;
      if (filterEndDate && messageDate > filterEndDate) return;
      
      const metrics = clarityMetrics || {};
      totalClarityScore += metrics.clarityScore || 0;
      totalComplexity += metrics.complexity || 0;
      totalQuestionFreq += metrics.questionFrequency || 0;
      totalActionItemFreq += metrics.actionItemFrequency || 0;
      totalDays++;
    });
    
    if (totalDays > 0) {
      const avgClarityScore = totalClarityScore / totalDays;
      const avgComplexity = totalComplexity / totalDays;
      const avgQuestionFreq = totalQuestionFreq / totalDays;
      const avgActionItemFreq = totalActionItemFreq / totalDays;
      
      // Generate insights based on metrics
      const insights = [];
      if (avgClarityScore > 0.8) insights.push('Very clear communication');
      else if (avgClarityScore > 0.6) insights.push('Clear communication');
      else if (avgClarityScore < 0.4) insights.push('Needs clarity improvement');
      
      if (avgComplexity > 0.7) insights.push('High complexity');
      else if (avgComplexity < 0.3) insights.push('Simple language');
      
      if (avgQuestionFreq > 0.6) insights.push('Many questions');
      if (avgActionItemFreq > 0.6) insights.push('Action-oriented');
      
      channelData.push({
        id: channelName,
        channel: channelName,
        score: Math.round(avgClarityScore * 100),
        complexity: Math.round(avgComplexity * 100),
        questionFrequency: Math.round(avgQuestionFreq * 100),
        actionItemFrequency: Math.round(avgActionItemFreq * 100),
        insights: insights.slice(0, 3), // Limit to 3 insights
        is_private: false // Will be enriched with actual value later
      });
    }
  });
  
  console.log(`Calculated ${channelData.length} channel clarity data points`);
  return channelData;
};

/**
 * Calculate sentiment distribution data with actual message counts from database report
 * @param {Object} reportData - The full report data from database
 * @param {string} startDate - Start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - End date for filtering (YYYY-MM-DD)
 * @returns {Array} Array of sentiment distribution data with message counts for charts
 */
export const calculateSentimentDistributionData = (reportData, startDate, endDate) => {
  console.log('calculateSentimentDistributionData called with:', { startDate, endDate });
  console.log('Available sentiment data keys:', Object.keys(reportData?.sentiment || {}));
  console.log('Using processed report data for sentiment calculation (no rawData)');
  
  if (!reportData?.sentiment?.sentimentDistribution) {
    console.log('No sentiment distribution data available in report');
    return [
      { name: "Positive", value: 0, messageCount: 0, color: "#14B882" },
      { name: "Neutral", value: 0, messageCount: 0, color: "#ABABBA" },
      { name: "Negative", value: 0, messageCount: 0, color: "#E50026" }
    ];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;
  
  let totalPositive = 0;
  let totalNeutral = 0;
  let totalNegative = 0;
  let totalDays = 0;
  let totalMessages = 0;
  
  console.log('Available sentiment distribution dates:', Object.keys(reportData.sentiment.sentimentDistribution));
  
  // Use estimated counts based on processed data
  console.log('Using estimated message counts from processed sentiment data');

  Object.entries(reportData.sentiment.sentimentDistribution).forEach(([dateStr, distribution]) => {
    const datePart = dateStr.split(',')[0].trim();
    const messageDate = new Date(datePart);
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    console.log(`Processing date ${dateStr}:`, distribution);
    
    // Use estimated message count since we don't store raw messages
    const estimatedDayMessages = 50; // Reasonable estimate for daily messages
    totalMessages += estimatedDayMessages;
    
    console.log(`  Estimated messages for ${datePart}: ${estimatedDayMessages}`);
    console.log(`  Distribution: positive=${distribution.positive}, neutral=${distribution.neutral}, negative=${distribution.negative}`);
    
    // Calculate sentiment counts based on estimated messages
    const positiveCount = Math.round((distribution.positive || 0) * estimatedDayMessages);
    const neutralCount = Math.round((distribution.neutral || 0) * estimatedDayMessages);
    const negativeCount = Math.round((distribution.negative || 0) * estimatedDayMessages);
    
    console.log(`  Sentiment counts: positive=${positiveCount}, neutral=${neutralCount}, negative=${negativeCount}`);
    
    totalPositive += positiveCount;
    totalNeutral += neutralCount;
    totalNegative += negativeCount;
    totalDays++;
  });
  
  console.log('Totals before calculation:', { totalPositive, totalNeutral, totalNegative, totalDays, totalMessages });
  
  if (totalDays === 0 || totalMessages === 0) {
    console.log('No data found, returning empty result');
    return [
      { name: "Positive", value: 0, messageCount: 0, color: "#14B882" },
      { name: "Neutral", value: 0, messageCount: 0, color: "#ABABBA" },
      { name: "Negative", value: 0, messageCount: 0, color: "#E50026" }
    ];
  }
  
  // Calculate percentages for chart display
  const actualTotal = totalPositive + totalNeutral + totalNegative;
  console.log('Actual total messages with sentiment:', actualTotal);
  
  const distributionData = [
    { 
      name: "Positive", 
      value: actualTotal > 0 ? Math.round((totalPositive / actualTotal) * 100) : 0, 
      messageCount: totalPositive,
      color: "#14B882" 
    },
    { 
      name: "Neutral", 
      value: actualTotal > 0 ? Math.round((totalNeutral / actualTotal) * 100) : 0, 
      messageCount: totalNeutral,
      color: "#ABABBA" 
    },
    { 
      name: "Negative", 
      value: actualTotal > 0 ? Math.round((totalNegative / actualTotal) * 100) : 0, 
      messageCount: totalNegative,
      color: "#E50026" 
    }
  ];
  
  console.log(`Final sentiment distribution result:`, distributionData);
  
  return distributionData;
}; 